import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { AdminKind } from "../api/queries";
import {
  useDeleteSubject, useImpact, useItemSearch, useMergeSubject, useTraderSearch,
} from "../api/queries";
import { Empty } from "./Empty";
import { ErrorBand } from "./ErrorBand";
import { Panel } from "./Panel";
import { TitleBand } from "./TitleBand";
import { itemHref, traderHref } from "../search/nav";

type Pending = { kind: "merge"; intoId: number; intoName: string } | { kind: "delete" };

/** Same debounce as the header's ItemSearch (see its comment for the
 *  reasoning): long enough that a burst of keystrokes is one request against
 *  the leading-wildcard LIKE instead of one per keystroke, short enough that
 *  the list is there by the time the eye arrives. */
const DEBOUNCE_MS = 150;

/** Merging and deleting, for an owner, on an item's or a trader's own page.
 *
 *  Both operations are irreversible, so everything here is arranged around the
 *  moment before the click: the counts come from the server, the confirmation
 *  names both records, and the delete path offers a merge instead — a mis-read
 *  name almost always wants merging, and offering the safer path where the
 *  decision is made beats any amount of warning text. */
export function AdminPanel({ kind, id }: { kind: AdminKind; id: number }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);

  // The typed value and the searched value are separate state, the same way
  // ItemSearch splits them: the input stays instant while the search behind it
  // lags a beat, instead of firing one request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const items = useItemSearch(kind === "items" ? query : "");
  const traders = useTraderSearch(kind === "traders" ? query : "");
  const matches = kind === "items"
    ? (items.data ?? []).map((m) => ({ id: m.id, label: m.canonical_name }))
    : (traders.data ?? []).map((m) => ({ id: m.id, label: m.nick }));

  // Gated on a confirmation actually being open, not just a live id: without
  // the `pending !== null` half this fires from every owner's item and trader
  // page on load, spending a round trip nobody asked for and warming the cache
  // so a delete dialog can paint page-load-era counts for one render.
  const impact = useImpact(
    kind, id, pending?.kind === "merge" ? pending.intoId : null, pending !== null);
  const merge = useMergeSubject(kind);
  const remove = useDeleteSubject(kind);

  const subject = kind === "items" ? "item" : "trader";
  const failed = merge.isError || remove.isError;
  const busy = merge.isPending || remove.isPending;

  function done(to: string) {
    setPending(null);
    setTerm("");
    setQuery("");
    navigate(to);
  }

  // The one way back to the picker from a confirmation. It must clear a failed
  // mutation's error state — without this the red band from a previous attempt
  // keeps floating over a picker that has moved on — and callers gate its
  // button on `busy` because returning to the picker while the DELETE or merge
  // it is cancelling out of is still running lets the page navigate out from
  // under whatever the picker goes on to show.
  function cancel() {
    merge.reset();
    remove.reset();
    setPending(null);
  }

  return (
    <>
      <TitleBand title="Admin" note="irreversible" />
      <Panel>
        {failed ? (
          <ErrorBand
            message={`Could not change this ${subject}.`}
            hint="Nothing was altered. Reload and try again."
          />
        ) : null}

        {pending === null ? (
          <div className="admin-controls">
            <label>
              Merge this {subject} into
              <input
                aria-label={`Search a ${subject} to merge into`}
                placeholder={kind === "items" ? "search an item…" : "search a nick…"}
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </label>
            {query === "" ? null : (
              <ul className="admin-matches">
                {/* The subject itself is excluded: merging it into itself is a
                    400 the picker can simply not offer. */}
                {matches.filter((m) => m.id !== id).length === 0 ? (
                  <li className="dim">Nothing matching “{query}”.</li>
                ) : (
                  matches.filter((m) => m.id !== id).map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => setPending({
                          kind: "merge", intoId: m.id, intoName: m.label,
                        })}
                      >
                        {m.label}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
            <button type="button" className="danger"
                    onClick={() => setPending({ kind: "delete" })}>
              Delete this {subject}
            </button>
          </div>
        ) : (
          <div className="admin-confirm">
            {impact.isError ? (
              <>
                <ErrorBand
                  message="Could not work out what this would affect."
                  hint="Nothing has been changed. Close this and try again."
                />
                {/* An irreversible action must never be a view you cannot
                    leave. Without this the only way out of a failed impact
                    fetch is reloading the page. */}
                <div className="admin-buttons">
                  <button type="button" onClick={cancel}>Cancel</button>
                </div>
              </>
            ) : !impact.data ? (
              <>
                <Empty>Counting what this would affect…</Empty>
                <div className="admin-buttons">
                  <button type="button" onClick={cancel}>Cancel</button>
                </div>
              </>
            ) : pending.kind === "merge" ? (
              <>
                {/* impact.data.name, not a prop: it is read from the server at
                    confirmation time, because a name this dialog only guessed
                    is not good enough for the last thing standing between an
                    owner and an irreversible act. */}
                <p>Merge “{impact.data.name}” into “{pending.intoName}”?</p>
                <ul className="dim">
                  <li>{impact.data.offers - (impact.data.collapses ?? 0)} offers move</li>
                  <li>{impact.data.collapses ?? 0} offers collapse into rows
                      “{pending.intoName}” already has</li>
                  <li>{impact.data.scans} scans are recounted</li>
                </ul>
                <p className="dim">
                  Future scans reading “{impact.data.name}” will resolve to
                  “{pending.intoName}”. This cannot be undone.
                </p>
                <div className="admin-buttons">
                  <button type="button" disabled={busy} onClick={cancel}>Cancel</button>
                  <button
                    type="button"
                    className="danger"
                    disabled={merge.isPending}
                    onClick={() => merge.mutate(
                      { id, into: pending.intoId },
                      { onSuccess: () => done(
                          kind === "items"
                            ? itemHref(pending.intoId, pending.intoName)
                            : traderHref(pending.intoName)) },
                    )}
                  >
                    Merge
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Delete “{impact.data.name}”?</p>
                <p className="dim">
                  {impact.data.offers} offers across {impact.data.scans} scans are
                  deleted permanently.
                </p>
                {/* Said separately because it is a different kind of loss: the
                    offers can be scanned again, and the vendor price cannot —
                    somebody typed it, and after this only the audit row
                    remembers the number. */}
                {impact.data.npc_price ? (
                  <p className="dim">
                    The vendor price recorded for this item goes with it. Nothing
                    rescans that number; only the audit row keeps it.
                  </p>
                ) : null}
                <p className="dim">
                  If this is a misread of another {subject}, merge instead — that
                  keeps the data and fixes future scans too.
                </p>
                <div className="admin-buttons">
                  <button type="button" disabled={busy} onClick={cancel}>Cancel</button>
                  <button type="button" disabled={busy} onClick={cancel}>
                    Merge instead
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(id, { onSuccess: () => done("/scans") })}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Panel>
    </>
  );
}
